import {
  NJUNS_TICKET_SEARCH_PATH,
  buildNjunsTicketSearch,
  getNjunsDisplayFields,
  getSelectedNjunsStatuses,
  isNjunsAuthError,
  isNjunsTestUrl,
  convertNjunsTicketsToFeatureCollection
} from '../../modules/NjunsApiLayerTranslation.js';

/**
 * @typedef {object} NjunsLayerConfig
 * @property {string} url The layer URL.
 * @property {object} [options]
 * @property {string} [options.fields] Comma-separated fields to include on each feature.
 * @property {Record<string, boolean>} [options.njuns_statuses] Ticket statuses to load.
 */

/**
 * @typedef {object} NjunsTicketLayerDependencies
 * @property {(path: string, requestOptions?: object) => Promise<any>} njunsApi Performs an NJUNS API request with the current token.
 * @property {(options: {testDatabase: boolean}) => Promise<boolean>} authenticate Prompts for sign in if needed and resolves to success.
 * @property {() => void} clearToken Discards the current token.
 */

/**
 * Loads an NJUNS layer's tickets in the map bounds, and converts them to drawable features.
 */
export class NjunsTicketLayerService {
  #njunsApi;
  #authenticate;
  #clearToken;

  /**
   * Create the service, throwing if a dependency is missing.
   * @param {NjunsTicketLayerDependencies} dependencies
   */
  constructor({ njunsApi, authenticate, clearToken } = {}) {
    if (typeof njunsApi !== 'function') throw new Error('njunsApi must be a function.');
    if (typeof authenticate !== 'function') throw new Error('authenticate must be a function.');
    if (typeof clearToken !== 'function') throw new Error('clearToken must be a function.');
    this.#njunsApi = njunsApi;
    this.#authenticate = authenticate;
    this.#clearToken = clearToken;
  }

  /**
   * One-time read of the layer's tickets in the bounds.
   * @param {NjunsLayerConfig} layerConfig
   * @param {import('../../modules/NjunsApiLayerTranslation.js').MapBounds} bounds The map bounds in degrees.
   * @returns {Promise<{type: 'FeatureCollection', features: object[]}|null>} The features, or null when there is nothing to load.
   */
  async fetchFeatures(layerConfig, bounds) {
    const statuses = getSelectedNjunsStatuses(layerConfig?.options?.njuns_statuses);
    if (statuses.length === 0) return null;

    const search = buildNjunsTicketSearch(bounds, statuses);
    const tickets = await this.#searchTickets(search, isNjunsTestUrl(layerConfig?.url));
    if (tickets == null) return null;
    return convertNjunsTicketsToFeatureCollection(tickets, getNjunsDisplayFields(layerConfig?.options?.fields));
  }

  // When NJUNS rejects the token, the user is asked to sign in again once before giving up
  async #searchTickets(search, testDatabase) {
    for (const isRetry of [false, true]) {
      if (isRetry) this.#clearToken();
      const isAuthenticated = await this.#authenticate({ testDatabase });
      if (!isAuthenticated) return null;
      const response = await this.#njunsApi(NJUNS_TICKET_SEARCH_PATH, { method: 'POST', data: search, testDatabase });
      if (!isNjunsAuthError(response)) return response;
    }
    return null;
  }
}
